import React from "react";
import { Edit } from "@mui/icons-material";
import { Alert, AlertTitle, Button } from "@mui/material";

import { Progress } from "ui";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  export: { label: "Exporting base data" },
  clean: { label: "Cleaning geometries" },
  ug_network: { label: "Generating UG network" },
  demand: { label: "Generating demands" },
  leadins: { label: "Generating leadins" },
  ug_costing: { label: "Calculating UG costing" },
  output: { label: "Preparing output" },
};

interface DataPrepPanelProps {
  isReadOnly?: boolean;
  canRun: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ isReadOnly, canRun }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { open } = useStackedNavigationContext();
  const { workflowState, stages, runWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.ISG.DATA_PREP,
  });

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleRunDataPrep = () => {
    runWorkflow?.();
  };

  const handleModifyData = () => {
    open("data-prep-draw");
  };

  const handleDrawBlockers = () => {
    open("data-prep-blockers-draw");
  };

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Upload required layers</AlertTitle>
          Please upload boundary and address layers before running data preparation.
        </Alert>
      )}

      {canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Data is ready for preparation.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation failed</AlertTitle>
          There was an error running data preparation. Please try again.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Data preparation complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {workflowState !== "COMPLETE" && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          disabled={!runWorkflow || !canRun || isInProgress || isReadOnly}
          onClick={handleRunDataPrep}
        >
          Start
        </Button>
      )}

      <StackedNavigationButton icon={<Edit />} title="Modify data" onClick={handleModifyData} sx={{ m: 0, mt: 1 }} />
    </>
  );
};

export default DataPrepPanel;
